import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bell, AlertTriangle, AlertCircle, Info, X } from "lucide-react";
import { notificationsApi, type AppNotification } from "../../api/notifications";
import { ApiError } from "../../api/client";

const POLL_INTERVAL_MS = 60_000;

/**
 * A 404 from the count endpoint means this server does not have the feature —
 * the desktop Rust server in local mode, or a cloud instance not yet deployed.
 * That is permanent for the life of the page, unlike a 500 or a dropped
 * connection, so it both hides the bell and stops the polling.
 */
function isUnsupported(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * The topbar bell.
 *
 * Panel-first on purpose: an owner is on some page, glances up, and wants to
 * know whether anything needs them — not to open a workspace and triage. The
 * full list page is for history.
 *
 * Opening the panel marks rows READ, which quiets the badge but leaves them
 * listed. Facebook clears the badge merely because you looked; for stock that
 * is wrong — someone glancing mid-rush has dealt with nothing, and the row
 * should still be there at closing time. Only "clear" (dismiss) removes it.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const countQuery = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: () => notificationsApi.unreadCount(),
    // A 404 means the server does not have this endpoint at all, and no amount
    // of waiting will change that — keep polling and it is a request a minute,
    // forever, against a server that will never answer. Any other error is
    // transient and should keep retrying on the interval.
    refetchInterval: (query) =>
      isUnsupported(query.state.error) ? false : POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });

  // A server that predates this feature — the desktop Rust server in local
  // mode, or a cloud instance not yet deployed. Hide the bell rather than
  // showing one that errors on every click.
  const unsupported = isUnsupported(countQuery.error);

  const listQuery = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => notificationsApi.list({ status: "open" }),
    enabled: open && !unsupported,
    retry: false,
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "count"] });
    },
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Marking read is driven by what the panel actually SHOWED, not by the badge
  // count: the badge may still be in flight when the bell is clicked, and a
  // "seen" that depends on a race is one an owner cannot reason about. The ref
  // keeps it to once per opening — the rows keep their unread flag until the
  // list is refetched, so a plain data-dependency would fire every render.
  const markedThisOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      markedThisOpen.current = false;
      return;
    }
    const rows = listQuery.data?.notifications;
    if (!rows || markedThisOpen.current) return;
    if (rows.some((n) => !n.read_at)) {
      markedThisOpen.current = true;
      markAllRead.mutate();
    }
    // markAllRead is stable for the life of the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listQuery.data]);

  if (unsupported) return null;

  const unread = countQuery.data?.unread_count ?? 0;
  const notifications = listQuery.data?.notifications ?? [];

  const handleToggle = () => setOpen(!open);

  const handleRowClick = (n: AppNotification) => {
    if (!n.link) return;
    setOpen(false);
    navigate(n.link);
  };

  return (
    <div className="relative">
      {open && (
        <div
          className="fixed inset-0 z-45 cursor-default"
          onClick={() => setOpen(false)}
        />
      )}

      <button
        onClick={handleToggle}
        aria-label={t("notifications.title")}
        className="relative flex items-center justify-center min-h-10 min-w-10 sm:min-h-0 sm:min-w-0 p-2 rounded-md hover:bg-sidebar-hover/10 transition-colors"
      >
        <Bell className="h-5 w-5 text-text-muted" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* On a phone the bell sits left of the language and user menus, so a
          panel hung from it runs off the left edge: there it spans the
          screen under the top bar. From sm up, it hangs from the bell. */}
      {open && (
        <div className="fixed inset-x-4 top-16 flex flex-col max-h-[calc(100dvh-5rem)] sm:block sm:max-h-none sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[22rem] sm:max-w-[calc(100vw-2rem)] bg-surface border border-border rounded-md shadow-lg z-50 origin-top-right animate-in fade-in slide-in-from-top-1 duration-100">
          <div className="flex items-center justify-between px-4 py-2 border-b border-border">
            <p className="text-sm font-medium text-text">{t("notifications.title")}</p>
            <button
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
              className="text-xs text-brand hover:underline"
            >
              {t("notifications.seeAll")}
            </button>
          </div>

          <div className="min-h-0 overflow-y-auto sm:max-h-96">
            {listQuery.isLoading && (
              <p className="px-4 py-6 text-sm text-text-muted text-center">
                {t("notifications.loading")}
              </p>
            )}

            {!listQuery.isLoading && notifications.length === 0 && (
              <p className="px-4 py-6 text-sm text-text-muted text-center">
                {t("notifications.empty")}
              </p>
            )}

            {notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onOpen={() => handleRowClick(n)}
                onDismiss={() => dismiss.mutate(n.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function severityIcon(severity: string) {
  if (severity === "critical") return <AlertCircle className="h-4 w-4 text-red-600" />;
  if (severity === "warning") return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  return <Info className="h-4 w-4 text-text-muted" />;
}

function NotificationRow({
  notification,
  onOpen,
  onDismiss,
}: {
  notification: AppNotification;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  // The server sends type + params, never a sentence. An unknown type (a server
  // newer than this bundle) falls back to a generic line instead of rendering
  // the raw key at the owner.
  const text = t(`notifications.types.${notification.type}`, {
    ...notification.params,
    defaultValue: t("notifications.unknownType"),
  });

  return (
    <div className="flex items-start gap-2 px-4 py-3 border-b border-border last:border-b-0 hover:bg-sidebar-hover/10 transition-colors">
      <span className="mt-0.5 shrink-0">{severityIcon(notification.severity)}</span>

      <button onClick={onOpen} className="flex-1 text-left min-w-0">
        <p className="text-sm text-text">{text}</p>
        <p className="text-xs text-text-muted mt-0.5">
          {relativeTime(notification.created_at, t)}
        </p>
      </button>

      <button
        onClick={onDismiss}
        aria-label={t("notifications.clear")}
        className="shrink-0 p-1 rounded hover:bg-sidebar-hover/20 text-text-muted"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Coarse on purpose — "2 hours ago" is all an owner needs from this list. */
function relativeTime(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return t("notifications.justNow");
  if (minutes < 60) return t("notifications.minutesAgo", { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t("notifications.hoursAgo", { n: hours });
  return t("notifications.daysAgo", { n: Math.round(hours / 24) });
}
